/* Imports */
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { makeServiceRequest } from '@garmin/axios-request';

/**
 * This is a wrapper helper to perform http requests using Axios.
 * You simply import it into your component and use it like this:
 * request({
 *  url: <THE_API_URL>              // This is required and will fail if not sent
 *  method: 'GET' / 'POST' / 'PUT'  // By default it is GET
 *  payload: ....                   // Used only for POST / PUT
 *  headers: {}                     // Custom headers that you want to set
 * });
 *
 * The function will return the output of the request
 * and you don't have to handle any Promises (usually).
 *
 *
 */
axiosRetry(axios, {
  retries: 3,
  shouldResetTimeout: true,
});

// Define a function to throw an error with a message
const throwError = (msg) => {
  throw new Error(`${msg}`);
};

/**
 * Make a global config object with default values.
 * @param {object} [config]     - Optional config override.
 * @returns {object}            - Merged configuration object with default values.
 */
const makeConfig = (config) => {
  const defaults = {
    method: 'GET',
    payload: undefined,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (!config.url) throwError('url is required to make request');

  return {
    ...defaults,
    ...config,
  };
};

// Add a cache-bypass parameter to the URL if needed
const setCacheBypass = (url) => {
  // Add cdncache param to all calls to bypass cache if set in the page url
  let newUrl = url;
  if (window.location.search.includes('cdncache=false')) {
    const urlObj = new URL(url);
    urlObj.searchParams.set('cdncache', 'false');
    newUrl = urlObj.href;
  }

  return newUrl;
};

/**
 * The main request functionality to make HTTP requests using Axios.
 * @param {config} config The config object
 * @returns {Promise<{data: object}>} The response body of the api request
 */
export const request = async (config = {}) => {
  const mergedConfig = makeConfig(config);

  const newUrl = setCacheBypass(mergedConfig.url);

  const opts = {
    method: mergedConfig.method,
    url: newUrl,
    headers: mergedConfig.headers,
    data: mergedConfig.payload,
  };

  const response = await makeServiceRequest(axios, opts)
    .catch((error) => {
      throwError(`Unable to call service - ${opts.url}, ${error?.message}`);
    });

  return response?.data;
};

export default request;
